import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { questionsApi } from '../../api/questions'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Card from '../../components/common/Card'
import Modal from '../../components/common/Modal'
import './Questions.css'

const Questions = () => {
  const { id } = useParams()
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [questionText, setQuestionText] = useState('')
  const [answerText, setAnswerText] = useState('')
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [showQuestionModal, setShowQuestionModal] = useState(false)
  const [showAnswerModal, setShowAnswerModal] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['course-questions', id],
    queryFn: () => questionsApi.getCourseQuestions(id),
  })

  const askMutation = useMutation({
    mutationFn: (data) => questionsApi.askQuestion(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['course-questions', id])
      setQuestionText('')
      setShowQuestionModal(false)
    },
  })

  const answerMutation = useMutation({
    mutationFn: (data) => questionsApi.postAnswer(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['course-questions', id])
      setAnswerText('')
      setShowAnswerModal(false)
      setSelectedQuestion(null)
    },
  })

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  const questions = data?.data?.questions || []

  const handleAskQuestion = (e) => {
    e.preventDefault()
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول لطرح سؤال')
      return
    }
    askMutation.mutate({
      course_id: id,
      content: questionText,
    })
  }

  const handleAnswer = (e) => {
    e.preventDefault()
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول للإجابة')
      return
    }
    answerMutation.mutate({
      question_id: selectedQuestion,
      content: answerText,
    })
  }

  return (
    <div className="questions-page bg-gray-900 min-h-screen">
      <Header />
      <main className="questions-main bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="questions-header">
            <h1 className="page-title text-white">الأسئلة والأجوبة</h1>
            {isAuthenticated && (
              <Button
                variant="primary"
                onClick={() => setShowQuestionModal(true)}
              >
                طرح سؤال
              </Button>
            )}
          </div>

          <div className="questions-list">
            {questions.map((question) => (
              <Card key={question.id} className="question-card">
                <div className="question-header">
                  <div className="question-author">
                    {question.user?.avatar ? (
                      <img src={getStorageUrl(question.user.avatar)} alt={question.user.name} className="author-avatar" />
                    ) : (
                      <div className="author-avatar-placeholder">
                        {question.user?.name?.[0] || 'U'}
                      </div>
                    )}
                    <div>
                      <h3 className="author-name">{question.user?.name || 'مستخدم'}</h3>
                      <span className="question-date">
                        {new Date(question.created_at).toLocaleDateString('ar-EG')}
                      </span>
                    </div>
                  </div>
                  {question.is_answered && (
                    <span className="answered-badge">تم الإجابة</span>
                  )}
                </div>
                <p className="question-content">{question.content}</p>
                
                {question.answers && question.answers.length > 0 && (
                  <div className="answers-section">
                    {question.answers.map((answer) => (
                      <div key={answer.id} className="answer-item">
                        <div className="answer-author">
                          {answer.user?.avatar ? (
                            <img src={getStorageUrl(answer.user.avatar)} alt={answer.user.name} className="answer-avatar" />
                          ) : (
                            <div className="answer-avatar-placeholder">
                              {answer.user?.name?.[0] || 'U'}
                            </div>
                          )}
                          <span className="answer-author-name">{answer.user?.name || 'مستخدم'}</span>
                        </div>
                        <p className="answer-content">{answer.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {isAuthenticated && (
                  <button
                    className="answer-button"
                    onClick={() => {
                      setSelectedQuestion(question.id)
                      setShowAnswerModal(true)
                    }}
                  >
                    إضافة إجابة
                  </button>
                )}
              </Card>
            ))}
            {questions.length === 0 && (
              <div className="empty-state">
                <p>لا توجد أسئلة</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Modal
        isOpen={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        title="طرح سؤال"
      >
        <form onSubmit={handleAskQuestion}>
          <Input
            label="السؤال"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="اكتب سؤالك هنا..."
            fullWidth
            required
          />
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <Button type="submit" variant="primary" fullWidth>
              إرسال
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowQuestionModal(false)}
              fullWidth
            >
              إلغاء
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showAnswerModal}
        onClose={() => {
          setShowAnswerModal(false)
          setSelectedQuestion(null)
        }}
        title="إضافة إجابة"
      >
        <form onSubmit={handleAnswer}>
          <Input
            label="الإجابة"
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            placeholder="اكتب إجابتك هنا..."
            fullWidth
            required
          />
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <Button type="submit" variant="primary" fullWidth>
              إرسال
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAnswerModal(false)
                setSelectedQuestion(null)
              }}
              fullWidth
            >
              إلغاء
            </Button>
          </div>
        </form>
      </Modal>

      <Footer />
    </div>
  )
}

export default Questions

